import React, {ReactNode} from 'react';
import I18n from 'app/i18n';
import {inject, observer} from 'mobx-react';
import {reaction, IReactionDisposer} from 'mobx';
import {PokerStore, TABLE_MINIMAL_LIMIT} from 'app/store/PokerStore';
import AsidePokerItem from './AsidePokerItem';
import ShowMore from './ShowMore';
import TableInterface from 'app/interfaces/poker/TableInterface';

interface Props {
    pokerStore?: PokerStore
}

interface State {
    renderedCount: number;
}

const TABLE_ITEM_HEIGHT = 40;

@inject('pokerStore')
@observer
export default class AsidePoker extends React.Component<Props, State> {
    state = {
        renderedCount: this.props.pokerStore.visibleCount
    };

    private tableItemsRef: React.RefObject<HTMLDivElement> = React.createRef();

    private disposeVisibleCount?: IReactionDisposer;

    componentDidMount(): void {
        const {pokerStore} = this.props;

        pokerStore.loadTables();

        this.disposeVisibleCount = reaction(
            () => pokerStore.visibleCount,
            (next: number, prev: number) => {
                if (next > prev) {
                    this.setState({renderedCount: next});
                }
            }
        );
    }

    handleTransitionEnd: React.TransitionEventHandler<HTMLDivElement> = e => {
        if (e.target === this.tableItemsRef.current && e.propertyName === 'height') {
            const {visibleCount} = this.props.pokerStore;

            if (visibleCount < this.state.renderedCount) {
                this.setState({renderedCount: visibleCount});
            }
        }
    };

    tablesToRender(): TableInterface[] {
        const isFullView = this.state.renderedCount > TABLE_MINIMAL_LIMIT;
        const {tables} = this.props.pokerStore;

        if (isFullView) {
            return [...tables].sort(PokerStore.byPlaymoneyBigBlindCount).slice(0, this.state.renderedCount);
        }

        return [...tables].sort(PokerStore.byPlaymoneyCountBigBlind).slice(0, this.state.renderedCount).sort(PokerStore.byPlaymoneyBigBlindCount);
    }

    render(): ReactNode {
        const {loadTable, hasMore, hasShowMore, showMore, visibleCount} = this.props.pokerStore;
        const height = `${visibleCount * TABLE_ITEM_HEIGHT}px`;
        const tablesToRender = this.tablesToRender();

        return <div className="aside-item poker-aside">
            <div className="aside-item-heading">
                <div className="aside-item-heading__title">
                    <h3>{I18n.t('poker.game')}</h3>
                </div>
            </div>
            <div className="poker-tables-list">
                <div className="poker-table-items" style={{height}} ref={this.tableItemsRef} onTransitionEnd={this.handleTransitionEnd}>
                    {tablesToRender.map((table, i) => <AsidePokerItem
                        key={`poker-table-${i}`}
                        name={`NL ${table.big_blind * 100}${table.playmoney ? ` ${I18n.t('shortcut_playmoney')}` : ''}`}
                        maxPlayers={table.max_players}
                        currentPlayers={table.count_players}
                        openTable={loadTable}
                        tableId={table.table_id}
                    />)}
                </div>
                {hasShowMore && <ShowMore onClickHandler={showMore} has_more={hasMore} />}
            </div>
        </div>;
    }
}
